import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Trophy,
  PencilSimple,
  CalendarBlank,
  ArrowRight
} from '@phosphor-icons/react';
import { format, parseISO } from 'date-fns';

const API = `${window.location.origin}/api`;

function WeeklySummary() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const res = await axios.get(`${API}/activity/weekly-summary`, { withCredentials: true });
        setData(res.data);
      } catch (error) {
        console.error('Failed to fetch weekly summary:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSummary();
  }, []);

  const formatTime = (ts) => {
    try { return format(parseISO(ts), 'EEE h:mm a'); }
    catch { return ''; }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-slate-900 border-t-transparent"></div>
      </div>
    );
  }

  if (!data) {
    return <p className="text-slate-500 text-center py-8">Failed to load summary.</p>;
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="weekly-summary">
      {/* Header — email-ready */}
      <div className="bg-navy text-white rounded-lg p-6 mb-6" data-testid="summary-header">
        <h2 className="text-xl font-bold mb-1">Your Helm Core week in review</h2>
        <p className="text-sm text-slate-400">
          {data.week_start} &mdash; {data.week_end}
        </p>
      </div>

      {/* Section 1: What you got done */}
      <div className="bg-white border border-slate-200 rounded-lg p-5 mb-4" data-testid="summary-completed">
        <div className="flex items-center gap-2 mb-4">
          <Trophy size={20} weight="fill" className="text-amber-500" />
          <h3 className="text-base font-semibold text-slate-900">Here's what you got done</h3>
        </div>

        <div className="flex items-center gap-4 mb-4">
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 text-center">
            <p className="text-2xl font-bold text-emerald-700">{data.completed.count}</p>
            <p className="text-xs text-emerald-600 font-medium">Completed</p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-center">
            <p className="text-2xl font-bold text-blue-700">{data.activity.tasks_touched}</p>
            <p className="text-xs text-navy font-medium">Tasks Touched</p>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded-lg px-4 py-3 text-center">
            <p className="text-2xl font-bold text-purple-700">{data.activity.total_updates}</p>
            <p className="text-xs text-purple-600 font-medium">Total Updates</p>
          </div>
        </div>

        {data.completed.tasks.length > 0 && (
          <div className="space-y-2">
            {data.completed.tasks.map((t, i) => (
              <div key={i} className="flex items-center gap-2 text-sm">
                <span className="text-emerald-600">&#10003;</span>
                <span className="text-slate-900 font-medium">{t.title}</span>
                {t.project_client && <span className="text-xs text-navy bg-blue-50 px-2 py-0.5 rounded">{t.project_client}</span>}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Section 2: Tasks you updated */}
      <div className="bg-white border border-slate-200 rounded-lg p-5 mb-4" data-testid="summary-activity">
        <div className="flex items-center gap-2 mb-4">
          <PencilSimple size={20} weight="fill" className="text-navy" />
          <h3 className="text-base font-semibold text-slate-900">Tasks you updated</h3>
        </div>

        {data.activity.top_logs.length === 0 ? (
          <p className="text-sm text-slate-500">No activity this week.</p>
        ) : (
          <div className="space-y-2.5">
            {data.activity.top_logs.map((log, i) => (
              <div key={i} className="flex items-start gap-3 text-sm">
                <span className="text-xs text-slate-400 font-mono w-20 flex-shrink-0 pt-0.5">{formatTime(log.time)}</span>
                <div className="flex-1 min-w-0">
                  <span className="font-medium text-slate-900">{log.task_title}</span>
                  {log.project_client && <span className="text-xs text-navy ml-2">[{log.project_client}]</span>}
                  <p className="text-slate-600 mt-0.5">{log.summary}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Section 3: Coming up next week */}
      <div className="bg-white border border-slate-200 rounded-lg p-5 mb-4" data-testid="summary-upcoming">
        <div className="flex items-center gap-2 mb-4">
          <CalendarBlank size={20} weight="fill" className="text-slate-700" />
          <h3 className="text-base font-semibold text-slate-900">Coming up next week</h3>
        </div>

        {data.upcoming.count === 0 ? (
          <p className="text-sm text-slate-500">No tasks due in the next 7 days.</p>
        ) : (
          <div className="space-y-2">
            {data.upcoming.tasks.map((t, i) => (
              <div key={i} className="flex items-center gap-3 p-3 border border-slate-100 rounded-lg">
                <CalendarBlank size={16} className="text-slate-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900">{t.title}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    {t.project_client && <span className="text-xs text-navy">{t.project_client}</span>}
                    <span className="text-xs text-slate-500">Due: {t.due_date}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-3 flex-wrap" data-testid="summary-actions">
        <button
          onClick={() => navigate('/tasks')}
          className="bg-navy text-white hover:bg-navy-hover transition-colors font-medium px-4 py-2 rounded-lg flex items-center gap-2 text-sm"
          data-testid="summary-view-tasks"
        >
          View All Tasks <ArrowRight size={14} />
        </button>
      </div>
    </motion.div>
  );
}

export default WeeklySummary;
